import React, { useState } from "react";
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// Mock data for tickets
const mockTickets = [
  {
    id: "TK-1023",
    subject: "Payroll mismatch for January",
    category: "Payroll",
    priority: "Critical",
    status: "In Progress",
    assignedTo: "Priya Sharma",
    creationDate: new Date().toISOString().split('T')[0],
    closedDate: null,
    feedback: null,
    conversation: [
      { sender: "John Doe", message: "My January payroll shows incorrect amount", timestamp: "2024-01-20 09:30" },
      { sender: "Priya Sharma", message: "I'll check your payroll calculation", timestamp: "2024-01-20 10:15" },
      { sender: "Priya Sharma", message: "Working on resolving the calculation error", timestamp: "2024-01-21 14:20" }
    ]
  },
  {
    id: "TK-0942",
    subject: "Unable to punch attendance",
    category: "Technical",
    priority: "Medium",
    status: "Assigned",
    assignedTo: "Arjun Singh",
    creationDate: new Date().toISOString().split('T')[0],
    closedDate: null,
    feedback: null,
    conversation: [
      { sender: "Jane Smith", message: "Attendance system not working on mobile app", timestamp: "2024-01-20 08:45" }
    ]
  },
  {
    id: "TK-0881",
    subject: "Reset VPN access",
    category: "IT Support",
    priority: "Low",
    status: "Resolved",
    assignedTo: "Rahul Mehta",
    creationDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    feedback: "Excellent support",
    conversation: [
      { sender: "Mike Johnson", message: "Need VPN access reset for remote work", timestamp: "2024-01-10 11:00" },
      { sender: "Rahul Mehta", message: "I'll reset your VPN credentials", timestamp: "2024-01-10 11:30" },
      { sender: "Rahul Mehta", message: "VPN access has been reset. Please try logging in with new credentials sent via email", timestamp: "2024-01-12 09:15" },
      { sender: "Mike Johnson", message: "Working perfectly now. Thank you!", timestamp: "2024-01-12 10:00" }
    ]
  },
  {
    id: "TK-0756",
    subject: "Leave balance inquiry",
    category: "HR",
    priority: "Low",
    status: "Closed",
    assignedTo: "Priya Sharma",
    creationDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    feedback: "Quick resolution",
    conversation: [
      { sender: "Sarah Wilson", message: "Can you check my remaining leave balance?", timestamp: "2024-01-08 14:20" },
      { sender: "Priya Sharma", message: "You have 12 days of annual leave remaining", timestamp: "2024-01-09 09:00" }
    ]
  },
  {
    id: "TK-0623",
    subject: "System login issues",
    category: "Technical",
    priority: "High",
    status: "Closed",
    assignedTo: "Arjun Singh",
    creationDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    feedback: "Good support",
    conversation: [
      { sender: "David Brown", message: "Cannot login to HRMS system", timestamp: "2024-01-05 08:30" },
      { sender: "Arjun Singh", message: "Let me check your account status", timestamp: "2024-01-05 09:00" },
      { sender: "Arjun Singh", message: "Your account was locked. I've unlocked it and reset your password", timestamp: "2024-01-07 10:30" }
    ]
  },
  {
    id: "TK-1156",
    subject: "Salary certificate request",
    category: "HR",
    priority: "Medium",
    status: "On Hold",
    assignedTo: "Priya Sharma",
    creationDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: null,
    feedback: null,
    conversation: [
      { sender: "Alex Kumar", message: "I need a salary certificate for loan application", timestamp: "2024-01-19 10:30" },
      { sender: "Priya Sharma", message: "I'll prepare your salary certificate", timestamp: "2024-01-19 11:00" }
    ]
  },
  {
    id: "TK-1089",
    subject: "Password reset request",
    category: "Technical",
    priority: "Low",
    status: "Closed",
    assignedTo: "Arjun Singh",
    creationDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    feedback: "Very fast response",
    conversation: [
      { sender: "Lisa Chen", message: "Forgot my password, need reset", timestamp: "2024-01-15 14:00" },
      { sender: "Arjun Singh", message: "Password reset link sent to your email", timestamp: "2024-01-15 14:05" },
      { sender: "Lisa Chen", message: "Got it, thanks!", timestamp: "2024-01-15 14:10" }
    ]
  },
  {
    id: "TK-0998",
    subject: "Expense reimbursement query",
    category: "Finance",
    priority: "Medium",
    status: "Waiting on Customer",
    assignedTo: "Rahul Mehta",
    creationDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: null,
    feedback: null,
    conversation: [
      { sender: "Tom Wilson", message: "My expense claim is pending for 2 weeks", timestamp: "2024-01-18 16:30" }
    ]
  },
  {
    id: "TK-0887",
    subject: "Training enrollment issue",
    category: "Training",
    priority: "Low",
    status: "Escalated",
    assignedTo: "Priya Sharma",
    creationDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    closedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    feedback: "Helpful guidance",
    conversation: [
      { sender: "Emma Davis", message: "Cannot enroll in the new training program", timestamp: "2024-01-12 09:15" },
      { sender: "Priya Sharma", message: "Let me check your training eligibility", timestamp: "2024-01-12 10:00" },
      { sender: "Priya Sharma", message: "You're now enrolled. Check your training dashboard", timestamp: "2024-01-14 11:30" }
    ]
  },
  {
    id: "TK-0776",
    subject: "Hardware replacement request",
    category: "IT Support",
    priority: "High",
    status: "Awaiting Parts/Resources",
    assignedTo: "Arjun Singh",
    creationDate: new Date().toISOString().split('T')[0],
    closedDate: null,
    feedback: null,
    conversation: [
      { sender: "Robert Lee", message: "My laptop screen is cracked, need replacement", timestamp: "2024-01-17 13:45" },
      { sender: "Arjun Singh", message: "I'll arrange a replacement laptop for you", timestamp: "2024-01-17 14:00" },
      { sender: "Arjun Singh", message: "Replacement laptop will be delivered tomorrow", timestamp: "2024-01-18 10:30" }
    ]
  }
];

export default function TicketDashboard() {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState('monthly');
  const [selectedTickets, setSelectedTickets] = useState(mockTickets);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Filter tickets based on date range
  const filterTicketsByDate = (tickets, filter) => {
    const now = new Date();
    const filtered = tickets.filter(ticket => {
      const creationDate = new Date(ticket.creationDate);
      
      switch(filter) {
        case 'daily':
          const today = new Date();
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return creationDate >= yesterday;
        case 'weekly':
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return creationDate >= weekAgo;
        case 'monthly':
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return creationDate >= monthAgo;
        case 'quarterly':
          const quarterAgo = new Date();
          quarterAgo.setMonth(quarterAgo.getMonth() - 3);
          return creationDate >= quarterAgo;
        case 'halfyearly':
          const halfYearAgo = new Date();
          halfYearAgo.setMonth(halfYearAgo.getMonth() - 6);
          return creationDate >= halfYearAgo;
        case 'yearly':
          const yearAgo = new Date();
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return creationDate >= yearAgo;
        case 'custom':
          if (customDateRange.start && customDateRange.end) {
            const startDate = new Date(customDateRange.start);
            const endDate = new Date(customDateRange.end);
            return creationDate >= startDate && creationDate <= endDate;
          }
          return true;
        default:
          return true;
      }
    });
    return filtered;
  };

  // Handle ticket click
  const handleTicketClick = (ticket) => {
    navigate('http://localhost:3000/admin/help-desk');
  };

  // Download ticket conversation report
  const downloadTicketReport = (ticket) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('Ticket Resolution Report', 20, 20);
    
    // Ticket details
    doc.setFontSize(12);
    doc.text(`Ticket ID: ${ticket.id}`, 20, 40);
    doc.text(`Subject: ${ticket.subject}`, 20, 50);
    doc.text(`Category: ${ticket.category}`, 20, 60);
    doc.text(`Priority: ${ticket.priority}`, 20, 70);
    doc.text(`Assigned To: ${ticket.assignedTo}`, 20, 80);
    doc.text(`Creation Date: ${ticket.creationDate}`, 20, 90);
    doc.text(`Closed Date: ${ticket.closedDate}`, 20, 100);
    doc.text(`Feedback: ${ticket.feedback || 'No feedback'}`, 20, 110);
    
    // Conversation
    doc.text('Conversation History:', 20, 130);
    let yPos = 140;
    
    ticket.conversation.forEach((msg, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(`${msg.timestamp} - ${msg.sender}:`, 20, yPos);
      yPos += 10;
      const splitText = doc.splitTextToSize(msg.message, 170);
      doc.text(splitText, 20, yPos);
      yPos += splitText.length * 10 + 5;
    });
    
    doc.save(`ticket-${ticket.id}-report.pdf`);
  };

  // Download Excel report
  const downloadExcelReport = () => {
    const filteredTickets = filterTicketsByDate(selectedTickets, dateFilter);
    const excelData = filteredTickets.map(ticket => ({
      'Creation Date': ticket.creationDate,
      'Ticket ID': ticket.id,
      'Subject': ticket.subject,
      'Category': ticket.category,
      'Priority': ticket.priority,
      'Status': ticket.status,
      'Assigned To': ticket.assignedTo,
      'Closed Date': ticket.closedDate || 'N/A',
      'Feedback': ticket.feedback || 'N/A'
    }));
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tickets Report');
    
    const fileName = dateFilter === 'custom' && customDateRange.start && customDateRange.end 
      ? `tickets-report-${customDateRange.start}-to-${customDateRange.end}.xlsx`
      : `tickets-report-${dateFilter}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
  };

  // Download PDF report
  const downloadPDFReport = () => {
    const filteredTickets = filterTicketsByDate(selectedTickets, dateFilter);
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Tickets Report', 20, 20);
    
    doc.setFontSize(12);
    const periodText = dateFilter === 'custom' && customDateRange.start && customDateRange.end 
      ? `Custom Range: ${customDateRange.start} to ${customDateRange.end}`
      : `${dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}`;
    doc.text(`Report Period: ${periodText}`, 20, 35);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 45);
    
    const tableData = filteredTickets.map(ticket => [
      ticket.creationDate,
      ticket.id,
      ticket.subject,
      ticket.category,
      ticket.priority,
      ticket.status,
      ticket.assignedTo,
      ticket.closedDate || 'N/A',
      ticket.feedback || 'N/A'
    ]);
    
    autoTable(doc, {
      head: [['Creation Date', 'Ticket ID', 'Subject', 'Category', 'Priority', 'Status', 'Assigned To', 'Closed Date', 'Feedback']],
      body: tableData,
      startY: 55,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] }
    });
    
    const fileName = dateFilter === 'custom' && customDateRange.start && customDateRange.end 
      ? `tickets-report-${customDateRange.start}-to-${customDateRange.end}.pdf`
      : `tickets-report-${dateFilter}.pdf`;
    
    doc.save(fileName);
  };

  // Filter tickets based on search and priority
  const getFilteredTickets = () => {
    let filtered = filterTicketsByDate(mockTickets, dateFilter);
    
    if (searchTerm) {
      filtered = filtered.filter(ticket => 
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (priorityFilter !== 'All') {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }
    
    return filtered;
  };

  const filteredTickets = getFilteredTickets();
  return (
    <div
      className="min-h-screen bg-gray-100 px-4 py-6 md:px-10"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      {/* HEADER */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ticket Dashboard</h1>
          <p className="text-gray-600 text-sm mt-1">
            Live ticket status, team workload & response metrics.
          </p>
        </div>
        
        {/* Download Reports */}
        <div className="flex gap-2">
          <button
            onClick={downloadPDFReport}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            PDF Report
          </button>
          <button
            onClick={downloadExcelReport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Excel Report
          </button>
        </div>
      </div>

      {/* DATE FILTER TABS */}
      <div className="bg-white shadow rounded-xl p-4 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <CalendarDaysIcon className="h-6 w-6 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Report Period</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {['daily', 'weekly', 'monthly', 'quarterly', 'halfyearly', 'yearly'].map((period) => (
            <button
              key={period}
              onClick={() => {
                setDateFilter(period);
                setShowCustomDatePicker(false);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                dateFilter === period
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
          <button
            onClick={() => {
              setDateFilter('custom');
              setShowCustomDatePicker(true);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              dateFilter === 'custom'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Custom Range
          </button>
        </div>
        
        {/* Custom Date Range Picker */}
        {showCustomDatePicker && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex gap-4 items-center">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                onClick={() => {
                  if (customDateRange.start && customDateRange.end) {
                    setDateFilter('custom');
                  }
                }}
                disabled={!customDateRange.start || !customDateRange.end}
                className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TOP STATISTICS */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
        <StatsCard
          title="Open/New Tickets"
          value={filteredTickets.filter(t => ['New', 'Open'].includes(t.status)).length.toString()}
          icon={ClipboardDocumentListIcon}
          color="bg-blue-600"
        />
        <StatsCard
          title="In Progress"
          value={filteredTickets.filter(t => ['Assigned', 'In Progress', 'Working'].includes(t.status)).length.toString()}
          icon={ClockIcon}
          color="bg-yellow-500"
        />
        <StatsCard
          title="Resolved/Closed"
          value={filteredTickets.filter(t => ['Resolved', 'Closed'].includes(t.status)).length.toString()}
          icon={BoltIcon}
          color="bg-green-600"
        />
        <StatsCard
          title="Total Tickets"
          value={filteredTickets.length.toString()}
          icon={ArrowTrendingUpIcon}
          color="bg-indigo-600"
        />
      </div>

      {/* FILTER BAR */}
      <div className="bg-white shadow rounded-xl p-4 mb-8 flex flex-col md:flex-row items-center justify-between gap-4">

        {/* Search */}
        <div className="relative w-full md:w-1/3">
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
            style={{ fontFamily: "Roboto" }}
          />
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
        </div>

        {/* Priority Filter */}
        <div className="flex gap-3 w-full md:w-auto">

          <PriorityButton 
            label="All" 
            selected={priorityFilter === 'All'} 
            onClick={() => setPriorityFilter('All')}
          />
          <PriorityButton 
            label="Critical" 
            color="text-red-800 border-red-800" 
            selected={priorityFilter === 'Critical'}
            onClick={() => setPriorityFilter('Critical')}
          />
          <PriorityButton 
            label="High" 
            color="text-red-600 border-red-600" 
            selected={priorityFilter === 'High'}
            onClick={() => setPriorityFilter('High')}
          />
          <PriorityButton 
            label="Medium" 
            color="text-yellow-600 border-yellow-600" 
            selected={priorityFilter === 'Medium'}
            onClick={() => setPriorityFilter('Medium')}
          />
          <PriorityButton 
            label="Low" 
            color="text-green-600 border-green-600" 
            selected={priorityFilter === 'Low'}
            onClick={() => setPriorityFilter('Low')}
          />

        </div>

        {/* Filter Icon */}
        <button className="flex items-center px-4 py-2 border rounded-lg text-gray-700 hover:bg-indigo-50">
          <FunnelIcon className="h-5 w-5 mr-2" />
          More Filters
        </button>

      </div>

      {/* MAIN CONTENT GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

        {/* TICKET TABLE */}
        <div className="xl:col-span-2 bg-white shadow rounded-xl p-6 overflow-auto">
          <h2 className="text-xl font-semibold mb-4">Tickets Report</h2>

          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="py-3 px-4 font-medium">Creation Date</th>
                <th className="py-3 px-4 font-medium">Ticket ID</th>
                <th className="py-3 px-4 font-medium">Subject</th>
                <th className="py-3 px-4 font-medium">Category</th>
                <th className="py-3 px-4 font-medium">Priority</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium">Assigned To</th>
                <th className="py-3 px-4 font-medium">Closed Date</th>
                <th className="py-3 px-4 font-medium">Feedback</th>
              </tr>
            </thead>

            <tbody className="text-gray-700">
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan="9" className="py-8 px-4 text-center text-gray-500">
                    No tickets found for the selected period
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    onClick={() => handleTicketClick(ticket)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* RIGHT SIDE PANEL */}
        <div className="bg-white shadow rounded-xl p-6 h-fit">

          <h2 className="text-xl font-semibold mb-4">Team Load Overview</h2>

          <div className="space-y-4">

            {/* Agent 1 */}
            <LoadItem name="Priya Sharma" load={76} />

            {/* Agent 2 */}
            <LoadItem name="Arjun Singh" load={52} />

            {/* Agent 3 */}
            <LoadItem name="Rahul Mehta" load={34} />

          </div>

          <hr className="my-6" />

          <h2 className="text-xl font-semibold mb-4">Categories Breakdown</h2>

          <CategoryItem label="HR Queries" percent={42} />
          <CategoryItem label="Technical Issues" percent={31} />
          <CategoryItem label="Payroll Issues" percent={18} />
          <CategoryItem label="Other Support" percent={9} />

        </div>

      </div>
    </div>
  );
}

/* -------------------------- COMPONENTS -------------------------- */

const StatsCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl shadow p-6 flex items-center justify-between hover:shadow-lg transition">
    <div>
      <p className="text-gray-500 text-sm">{title}</p>
      <h2 className="text-3xl font-bold mt-1">{value}</h2>
    </div>
    <div className={`${color} p-3 rounded-lg`}>
      <Icon className="h-7 w-7 text-white" />
    </div>
  </div>
);

const PriorityButton = ({ label, selected, color, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
      selected
        ? "bg-indigo-600 text-white border-indigo-600"
        : `${color || "text-gray-600 border-gray-400"} hover:bg-gray-200`
    }`}
  >
    {label}
  </button>
);

const TicketRow = ({ ticket, onClick }) => {
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'Critical': return 'text-red-800';
      case 'High': return 'text-red-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'New':
      case 'Open': return 'text-blue-600';
      case 'Assigned': return 'text-purple-600';
      case 'In Progress':
      case 'Working': return 'text-yellow-600';
      case 'On Hold':
      case 'Pending': return 'text-orange-600';
      case 'Waiting on Customer':
      case 'Awaiting User': return 'text-pink-600';
      case 'Escalated': return 'text-red-600';
      case 'Resolved': return 'text-green-600';
      case 'Closed': return 'text-green-700';
      case 'Reopened': return 'text-blue-700';
      case 'Cancelled':
      case 'Duplicate': return 'text-gray-600';
      case 'Scheduled': return 'text-indigo-600';
      case 'Awaiting Parts/Resources': return 'text-amber-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <tr 
      className="border-b hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
    >
      <td className="py-3 px-4">{ticket.creationDate}</td>
      <td className="py-3 px-4 font-medium text-indigo-600">{ticket.id}</td>
      <td className="py-3 px-4">{ticket.subject}</td>
      <td className="py-3 px-4">{ticket.category}</td>
      <td className={`py-3 px-4 font-medium ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</td>
      <td className={`py-3 px-4 font-medium ${getStatusColor(ticket.status)}`}>{ticket.status}</td>
      <td className="py-3 px-4">{ticket.assignedTo}</td>
      <td className="py-3 px-4">{ticket.closedDate || 'N/A'}</td>
      <td className="py-3 px-4">{ticket.feedback || 'N/A'}</td>
    </tr>
  );
};

const LoadItem = ({ name, load }) => (
  <div>
    <div className="flex justify-between mb-1">
      <p className="text-gray-700 font-medium">{name}</p>
      <p className="text-gray-600 text-sm">{load}%</p>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-3">
      <div
        className="bg-indigo-600 h-3 rounded-full"
        style={{ width: `${load}%` }}
      ></div>
    </div>
  </div>
);

const CategoryItem = ({ label, percent }) => (
  <div className="mb-3">
    <p className="text-gray-700 font-medium">{label}</p>
    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
      <div
        className="bg-green-600 h-2 rounded-full"
        style={{ width: `${percent}%` }}
      />
    </div>
  </div>
);
